import {Component, OnDestroy, OnInit} from '@angular/core';
import {
    AgentSummary,
    CustomerSummary,
    MOCK_DASHBOARD_DATA,
    Order,
    OrderItem,
    OrderStatus,
    StatusCount
} from './dashboard.models';
import {Chart, registerables} from 'chart.js';
import {ModelProvider} from "../../../../framework/providers/modelProvider/ModelProvider";
import {AdminService} from "../../services/AdminService";
import {NavController} from "@ionic/angular";

Chart.register(...registerables);

@Component({
    selector: 'app-main-agents',
    templateUrl: './main-agents.page.html',
    styleUrls: ['./main-agents.page.scss'],
})
export class MainAgentsPage implements OnInit, OnDestroy {
    selectedTab: 'orders' | 'summary' | 'bonus' = 'orders';

    statusCount: StatusCount[] = [];
    orders: Order[] = [];
    agents: AgentSummary[] = [];
    statusList: OrderStatus[] = [];
    customers: CustomerSummary[] = [];
    saleItems: OrderItem[] = [];

    selectedDateRange: string = 'today';
    selectedAgent: string = 'all';
    searchQuery: string = '';
    selectedStatus: string = 'all';
    selectedPaymentType: string = 'all';
    selectedApprovalStatus: string = 'all';

    // Summary data
    daySummary = {
        count: 0,
        sumBeforeVat: 0,
        sumAfterVat: 0
    };

    monthSummary = {
        count: 0,
        sumBeforeVat: 0,
        sumAfterVat: 0
    };

    filteredOrders: Order[] = [];

    private todayChart: any = null;
    private monthChart: any = null;
    private threemonthChart: any = null;
    private chartTimeout: any = null;
    public isLoading = true;

    // Bonus dashboard charts
    private ordersCountChart: any = null;
    private topItemsChart: any = null;
    private topBrandsChart: any = null;
    private revenueChart: any = null;
    private ordersByStatusChart: any = null;

    constructor(private modelProvider: ModelProvider,
                private nav: NavController,
                private adminService: AdminService) {
    }

    getStatusTranslationKey(statusName: string): string {
        return statusName.toUpperCase().replace(/ /g, '_');
    }

    ngOnInit() {
        // Load mock data
        // this.orders = MOCK_DASHBOARD_DATA.orders;
        // this.agents = MOCK_DASHBOARD_DATA.agents;
        // this.statusList = MOCK_DASHBOARD_DATA.statusList;

        let me = this;
        // setTimeout(function (){
        //     me.isLoading = false;
        // }, 3000);

        this.modelProvider.custom("getDashboardManager").then(function (res) {
            // me.catalogData = res;
            me.isLoading = false;

            me.orders = res['orders'];
            me.agents = res['agents'];
            me.statusList = res['statusList'];
            me.customers = res['customers'];
            me.saleItems = res['saleItems'];
            me.statusCount = res['statusCount'];
            // me.customers = MOCK_DASHBOARD_DATA.customers || [];
            // me.saleItems = MOCK_DASHBOARD_DATA.saleItems || [];
            // me.orders = MOCK_DASHBOARD_DATA.orders;
            // me.agents = MOCK_DASHBOARD_DATA.agents;
            // me.statusList = MOCK_DASHBOARD_DATA.statusList;

            me.calculateSummary();
            me.applyFilters();

        })
    }

    ngAfterViewInit() {
        // Create charts after view is initialized
        this.chartTimeout = setTimeout(() => {
            if (this.selectedTab === 'summary') {
                this.createCharts();
            } else if (this.selectedTab === 'bonus') {
                this.createBonusCharts();
            }
        }, 100);
    }

    ngOnDestroy() {
        this.destroyCharts();
        if (this.chartTimeout) {
            clearTimeout(this.chartTimeout);
        }
    }

    onOrderClick(order) {
        this.adminService.openCustomModel(this.nav, 'prefix', null, 'order', null, null, order.id);
    }

    calculateSummary() {
        const today = new Date().toISOString().split('T')[0];
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();

        // Day summary
        const todayOrders = this.orders.filter(o => o.date === today);
        this.daySummary.count = todayOrders.length;
        this.daySummary.sumBeforeVat = todayOrders.reduce((sum, o) => sum + o.priceBeforeVat, 0);
        this.daySummary.sumAfterVat = this.daySummary.sumBeforeVat * 1.17; // 17% VAT

        // Month summary
        const monthOrders = this.orders.filter(o => {
            const orderDate = new Date(o.date);
            return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
        });
        this.monthSummary.count = monthOrders.length;
        this.monthSummary.sumBeforeVat = monthOrders.reduce((sum, o) => sum + o.priceBeforeVat, 0);
        this.monthSummary.sumAfterVat = this.monthSummary.sumBeforeVat * 1.17; // 17% VAT
    }

    selectTab(tab: 'orders' | 'summary' | 'bonus') {
        this.selectedTab = tab;
        if (tab === 'summary') {
            if (this.chartTimeout) {
                clearTimeout(this.chartTimeout);
            }
            this.chartTimeout = setTimeout(() => this.createCharts(), 100);
        } else if (tab === 'bonus') {
            if (this.chartTimeout) {
                clearTimeout(this.chartTimeout);
            }
            this.chartTimeout = setTimeout(() => this.createBonusCharts(), 100);
        } else {
            this.destroyCharts();
        }
    }

    getStatusCount(status: OrderStatus): number {
        return this.filteredOrders.filter(o => o.status.name === status.name).length;
    }

    onStatusClick(status: OrderStatus) {
        // TODO: Implement filtering by status
    }

    onDateRangeChange(range: string) {
        this.selectedDateRange = range;
        this.applyFilters();
    }

    onAgentChange() {
        this.applyFilters();
    }

    applyFilters() {
        let filtered = [...this.orders];

        // Filter by date range
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        switch (this.selectedDateRange) {
            case 'today':
                const todayStr = today.toISOString().split('T')[0];
                filtered = filtered.filter(o => o.date === todayStr);
                break;
            case 'thisMonth':
                const currentMonth = today.getMonth();
                const currentYear = today.getFullYear();
                filtered = filtered.filter(o => {
                    const orderDate = new Date(o.date);
                    return orderDate.getMonth() === currentMonth && orderDate.getFullYear() === currentYear;
                });
                break;
            case 'lastWeek':
                const lastWeek = new Date(today);
                lastWeek.setDate(lastWeek.getDate() - 7);
                filtered = filtered.filter(o => {
                    const orderDate = new Date(o.date);
                    return orderDate >= lastWeek && orderDate <= today;
                });
                break;
            case '3months':
                const threeMonthsAgo = new Date(today);
                threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                filtered = filtered.filter(o => {
                    const orderDate = new Date(o.date);
                    return orderDate >= threeMonthsAgo && orderDate <= today;
                });
                break;
        }

        // Filter by agent
        if (this.selectedAgent !== 'all') {
            filtered = filtered.filter(o => o.agentName === this.selectedAgent);
        }

        // Filter by status
        if (this.selectedStatus !== 'all') {
            filtered = filtered.filter(o => o.status.name === this.selectedStatus);
        }

        // Filter by payment type
        if (this.selectedPaymentType !== 'all') {
            filtered = filtered.filter(o => o.paymentType === this.selectedPaymentType);
        }

        // Filter by approval status
        if (this.selectedApprovalStatus !== 'all') {
            const isApproved = this.selectedApprovalStatus === 'approved';
            filtered = filtered.filter(o => o.approved === isApproved);
        }

        // Filter by search query
        if (this.searchQuery && this.searchQuery.trim() !== '') {
            const query = this.searchQuery.toLowerCase().trim();
            filtered = filtered.filter(o =>
                o.id.toString().includes(query) ||
                o.type.toLowerCase().includes(query) ||
                o.agentName.toLowerCase().includes(query) ||
                o.clientOrAgent.toLowerCase().includes(query) ||
                o.status.name.toLowerCase().includes(query)
            );
        }

        this.filteredOrders = filtered;
    }

    getUniqueAgents(): string[] {
        return Array.from(new Set(this.orders.map(o => o.agentName)));
    }

    getUniquePaymentTypes(): string[] {
        return Array.from(new Set(this.orders.map(o => o.paymentType)));
    }

    onAgentSelectChange(event: any) {
        this.applyFilters();
    }

    onSearchChange() {
        this.applyFilters();
    }

    onStatusFilterChange() {
        this.applyFilters();
    }

    onPaymentTypeChange() {
        this.applyFilters();
    }

    onApprovalStatusChange() {
        this.applyFilters();
    }

    createCharts() {
        this.destroyCharts();

        // Today pie chart
        const todayCanvas = document.getElementById('todayChart') as HTMLCanvasElement;
        if (todayCanvas) {
            const todayData = this.agents.map(a => a.todaySumAfterVat);
            const todayLabels = this.agents.map(a => a.name);

            this.todayChart = new Chart(todayCanvas, {
                type: 'pie',
                data: {
                    labels: todayLabels,
                    datasets: [{
                        data: todayData,
                        backgroundColor: [
                            '#FF6384',
                            '#36A2EB',
                            '#FFCE56',
                            '#4BC0C0',
                            '#9966FF',
                            '#FF9F40'
                        ]
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom'
                        },
                        title: {
                            display: true,
                            text: 'היום לפי סוכן'
                        }
                    }
                }
            });
        }

        // Month pie chart
        const monthCanvas = document.getElementById('monthChart') as HTMLCanvasElement;
        if (monthCanvas) {
            const monthData = this.agents.map(a => a.monthSumAfterVat);
            const monthLabels = this.agents.map(a => a.name);

            this.monthChart = new Chart(monthCanvas, {
                type: 'pie',
                data: {
                    labels: monthLabels,
                    datasets: [{
                        data: monthData,
                        backgroundColor: [
                            '#FF6384',
                            '#36A2EB',
                            '#FFCE56',
                            '#4BC0C0',
                            '#9966FF',
                            '#FF9F40'
                        ]
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom'
                        },
                        title: {
                            display: true,
                            text: 'מתחילת החודש לפי סוכן'
                        }
                    }
                }
            });
        }

        // threemonthChart pie chart
        const threemonthCanvas = document.getElementById('threemonthChart') as HTMLCanvasElement;
        if (threemonthCanvas) {
            const monthData = this.agents.map(a => a.threemonthSumAfterVat);
            const monthLabels = this.agents.map(a => a.name);

            this.threemonthChart = new Chart(threemonthCanvas, {
                type: 'pie',
                data: {
                    labels: monthLabels,
                    datasets: [{
                        data: monthData,
                        backgroundColor: [
                            '#FF6384',
                            '#36A2EB',
                            '#FFCE56',
                            '#4BC0C0',
                            '#9966FF',
                            '#FF9F40'
                        ]
                    }]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'bottom'
                        },
                        title: {
                            display: true,
                            text: '3 חודשים לפי סוכן'
                        }
                    }
                }
            });
        }
    }

    destroyCharts() {
        if (this.todayChart) {
            this.todayChart.destroy();
            this.todayChart = null;
        }
        if (this.monthChart) {
            this.monthChart.destroy();
            this.monthChart = null;
        }
        if (this.threemonthChart) {
            this.threemonthChart.destroy();
            this.threemonthChart = null;
        }

        if (this.ordersCountChart) {
            this.ordersCountChart.destroy();
            this.ordersCountChart = null;
        }
        if (this.topItemsChart) {
            this.topItemsChart.destroy();
            this.topItemsChart = null;
        }
        if (this.topBrandsChart) {
            this.topBrandsChart.destroy();
            this.topBrandsChart = null;
        }
        if (this.revenueChart) {
            this.revenueChart.destroy();
            this.revenueChart = null;
        }
        if (this.ordersByStatusChart) {
            this.ordersByStatusChart.destroy();
            this.ordersByStatusChart = null;
        }
    }

    createBonusCharts() {
        this.destroyCharts();

        // 1. Line graph of orders count by date
        this.createOrdersCountChart();

        // 2. Pie graph for top item sales
        this.createTopItemsChart();

        // 3. Pie graph for top brand
        this.createTopBrandsChart();

        // 4. Line graph – revenue by date
        this.createRevenueChart();

        // 5. Donut chart – orders by status
        this.createOrdersByStatusChart();
    }

    createOrdersCountChart() {
        const canvas = document.getElementById('ordersCountChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Group orders by date
        const ordersByDate = new Map<string, number>();
        this.orders.forEach(order => {
            const count = ordersByDate.get(order.date) || 0;
            ordersByDate.set(order.date, count + 1);
        });

        const sortedDates = Array.from(ordersByDate.keys()).sort();
        const counts = sortedDates.map(date => ordersByDate.get(date) || 0);

        this.ordersCountChart = new Chart(canvas, {
            type: 'line',
            data: {
                labels: sortedDates,
                datasets: [{
                    label: 'Orders Count',
                    data: counts,
                    borderColor: '#667eea',
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    tension: 0.4,
                    fill: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top'
                    },
                    title: {
                        display: true,
                        text: 'כמות הזמנות לפי תאריך'
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        }
                    }
                }
            }
        });
    }

    createTopItemsChart() {
        const canvas = document.getElementById('topItemsChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Aggregate item sales from saleItems
        const itemSales = new Map<string, number>();
        this.saleItems.forEach(item => {
            const currentQty = itemSales.get(item.name) || 0;
            itemSales.set(item.name, currentQty + item.quantity);
        });

        // Get top 5 items
        const sortedItems = Array.from(itemSales.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        const itemNames = sortedItems.map(item => item[0]);
        const quantities = sortedItems.map(item => item[1]);

        this.topItemsChart = new Chart(canvas, {
            type: 'pie',
            data: {
                labels: itemNames,
                datasets: [{
                    data: quantities,
                    backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF'
                    ]
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom'
                    },
                    title: {
                        display: true,
                        text: 'הפריטים המובילים'
                    }
                }
            }
        });
    }

    createTopBrandsChart() {
        const canvas = document.getElementById('topBrandsChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Aggregate brand sales from saleItems
        const brandSales = new Map<string, number>();
        this.saleItems.forEach(item => {
            const currentRevenue = brandSales.get(item.brand) || 0;
            brandSales.set(item.brand, currentRevenue + (item.quantity * item.price));
        });

        // Get top 5 brands
        const sortedBrands = Array.from(brandSales.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);

        const brandNames = sortedBrands.map(brand => brand[0]);
        const revenues = sortedBrands.map(brand => brand[1]);

        this.topBrandsChart = new Chart(canvas, {
            type: 'pie',
            data: {
                labels: brandNames,
                datasets: [{
                    data: revenues,
                    backgroundColor: [
                        '#FF6384',
                        '#36A2EB',
                        '#FFCE56',
                        '#4BC0C0',
                        '#9966FF'
                    ]
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom'
                    },
                    title: {
                        display: true,
                        text: 'המותגים המובילים'
                    }
                }
            }
        });
    }

    createRevenueChart() {
        const canvas = document.getElementById('revenueChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Group revenue by date
        const revenueByDate = new Map<string, number>();
        this.orders.forEach(order => {
            const revenue = revenueByDate.get(order.date) || 0;
            revenueByDate.set(order.date, revenue + (order.priceBeforeVat * 1.17));
        });

        const sortedDates = Array.from(revenueByDate.keys()).sort();
        const revenues = sortedDates.map(date => revenueByDate.get(date) || 0);

        this.revenueChart = new Chart(canvas, {
            type: 'line',
            data: {
                labels: sortedDates,
                datasets: [{
                    label: 'Revenue (₪)',
                    data: revenues,
                    borderColor: '#4CAF50',
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    tension: 0.4,
                    fill: true
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'top'
                    },
                    title: {
                        display: true,
                        text: 'סכום הזמנות לפי תאריך'
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true
                    }
                }
            }
        });
    }

    createOrdersByStatusChart() {
        const canvas = document.getElementById('ordersByStatusChart') as HTMLCanvasElement;
        if (!canvas) return;

        // Count orders by status (only approved, pending, rejected)
        const statusCounts = new Map<string, number>();
        console.log(this.statusCount);
        const keys = this.statusCount.map(item => item['status__name']);
        const values = this.statusCount.map(item => item['orderCount']);

        this.ordersByStatusChart = new Chart(canvas, {
            type: 'doughnut',
            data: {
                labels: keys,
                datasets: [{
                    data: values,
                    backgroundColor: [
                        '#4CAF50',
                        '#FFA500',
                        '#F44336'
                    ]
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        position: 'bottom'
                    },
                    title: {
                        display: true,
                        text: 'סטטוס הזמנות'
                    }
                }
            }
        });
    }
}
